import React, { useState, useEffect } from 'react';
import { X, Loader2, Copy, Check, Globe } from 'lucide-react';
import toast from 'react-hot-toast';
import { sellerAccountService } from '../../../services/sellerAccountService';

const COUNTRY_FLAGS = {
  IN: { flag: 'IN', name: 'India' },
  US: { flag: 'US', name: 'United States' },
  UK: { flag: 'GB', name: 'United Kingdom' },
  GB: { flag: 'GB', name: 'United Kingdom' },
  DE: { flag: 'DE', name: 'Germany' },
  JP: { flag: 'JP', name: 'Japan' },
  CA: { flag: 'CA', name: 'Canada' },
  FR: { flag: 'FR', name: 'France' },
  IT: { flag: 'IT', name: 'Italy' },
  ES: { flag: 'ES', name: 'Spain' },
  MX: { flag: 'MX', name: 'Mexico' },
  BR: { flag: 'BR', name: 'Brazil' },
  AE: { flag: 'AE', name: 'UAE' },
  SA: { flag: 'SA', name: 'Saudi Arabia' },
  AU: { flag: 'AU', name: 'Australia' },
  SG: { flag: 'SG', name: 'Singapore' },
};

function getFlagEmoji(countryCode) {
  if (!countryCode) return '';
  const code = countryCode.toUpperCase();
  return String.fromCodePoint(...[...code].map((c) => 0x1f1e6 + c.charCodeAt(0) - 65));
}

export default function MarketplaceModal({ isOpen, onClose, account }) {
  const [marketplaces, setMarketplaces] = useState([]);
  const [loading, setLoading] = useState(false);
  const [copiedId, setCopiedId] = useState(null);

  useEffect(() => {
    if (isOpen && account?.id) {
      fetchMarketplaces();
    }
  }, [isOpen, account?.id]);

  const fetchMarketplaces = async () => {
    setLoading(true);
    try {
      const data = await sellerAccountService.getMarketplaces(account.id);
      setMarketplaces(Array.isArray(data) ? data : data?.marketplaces || []);
    } catch (err) {
      toast.error('Failed to load marketplace participations');
      setMarketplaces([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async (marketplaceId) => {
    try {
      await navigator.clipboard.writeText(marketplaceId);
      setCopiedId(marketplaceId);
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      toast.error('Failed to copy');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-teal-100 flex items-center justify-center">
              <Globe size={18} className="text-teal-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-800">Marketplace Participations</h3>
              <p className="text-xs text-gray-500">
                {account?.sellerName || account?.sellingPartnerId || 'Seller Account'}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <X size={18} className="text-gray-500" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 size={32} className="text-[#f08804] animate-spin mb-3" />
              <p className="text-sm text-gray-500">Loading marketplaces...</p>
            </div>
          ) : marketplaces.length === 0 ? (
            <div className="text-center py-12">
              <Globe size={40} className="mx-auto text-gray-300 mb-3" />
              <p className="text-gray-500 font-medium">No marketplaces found</p>
              <p className="text-xs text-gray-400 mt-1">This account may not have marketplace participation data yet.</p>
            </div>
          ) : (
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {marketplaces.map((mp, idx) => {
                const countryCode = mp.countryCode || mp.country;
                const countryInfo = COUNTRY_FLAGS[countryCode] || {};
                const isParticipating = mp.isParticipating !== false;

                return (
                  <div
                    key={mp.marketplaceId || idx}
                    className={`flex items-center justify-between p-3 rounded-lg border ${
                      isParticipating ? 'border-gray-200 bg-white' : 'border-gray-100 bg-gray-50 opacity-60'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-xl leading-none">
                        {countryCode ? getFlagEmoji(countryInfo.flag || countryCode) : ''}
                      </span>
                      <div>
                        <p className="text-sm font-medium text-gray-800">
                          {countryInfo.name || mp.countryName || countryCode || 'Unknown'}
                        </p>
                        <p className="text-xs text-gray-400 font-mono">{mp.marketplaceId}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          isParticipating
                            ? 'bg-green-100 text-green-700'
                            : 'bg-gray-100 text-gray-500'
                        }`}
                      >
                        {isParticipating ? 'Active' : 'Inactive'}
                      </span>
                      <button
                        onClick={() => handleCopy(mp.marketplaceId)}
                        className="p-1.5 hover:bg-gray-100 rounded-md transition-colors"
                        title="Copy Marketplace ID"
                      >
                        {copiedId === mp.marketplaceId ? (
                          <Check size={14} className="text-green-500" />
                        ) : (
                          <Copy size={14} className="text-gray-400" />
                        )}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t bg-gray-50">
          <button
            onClick={onClose}
            className="w-full px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-lg transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
